import {
  Breadcrumb,
  Button,
  PageHeader,
  Table,
  Popconfirm,
  message,
  Tag,
  Input,
  Upload,
} from "antd";
import { CloseOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Page from "_components/Page";
import { companyService, ApplicationDocumentService } from "_services";
import { Utils } from "_helpers";
import TabMenu from "../TabMenu";
import { Auth, TiepDocumentType } from "_helpers";

function List({ match }) {
  const { id } = match.params;
  const { path } = match;
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState({});
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});

  useEffect(() => {
    setLoading(true);
    companyService
      .getById(id)
      .then((x) => {
        const company = x.content;
        setCompany(company);
        
        // Create table data showing all TiepDocumentType values
        const existingDocs = company.subApplicationDocuments || [];
        const allDocumentTypes = TiepDocumentType.map(docType => {
          const existingDoc = existingDocs.find(doc => doc.applicationDocumentType === docType.value);
          return {
            key: docType.value,
            documentType: docType.name,
            documentTypeValue: docType.value,
            documentName: existingDoc?.documentName || '',
            applicationStatus: existingDoc?.applicationStatus || '',
            checked: existingDoc?.checked || false,
            comment: existingDoc?.comment || '',
            existingDocument: existingDoc,
            hasDocument: !!existingDoc
          };
        });
        
        setDocumentData(allDocumentTypes);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(error);
      });
  }, []);

  const handleDownloadDocument = (record) => {
    if (!record?.id) return;
    ApplicationDocumentService.downloadSubDocument(record.id)
      .then((response) => {
        const fileNameFromPath = (record.documentLocation || "").split("/").pop();
        const safeName = record.documentName || fileNameFromPath || `document_${record.id}`;
        Utils.downloadFileFromBlob(response, safeName);
      })
      .catch(() => {});
  };

  const handleFileUpload = (file, documentTypeValue) => {
    setUploadingFiles(prev => ({ ...prev, [documentTypeValue]: true }));
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onload = function(e) {
      const base64File = e.target.result;
      const fileName = file.name;
      
      const payload = {
        applicationDocumentType: documentTypeValue,
        documentName: fileName,
        originalFileName: fileName,
        base64FileString: base64File,
        comment: `Uploaded ${new Date().toLocaleDateString()}`,
        applicationStatus: "APPLICATION",
        checked: false,
        parentApplication: null // This is a sub-document for the company
      };
      
      // Use the create endpoint to upload the document
      ApplicationDocumentService.uploadSubDocument(id, payload)
        .then((response) => {
          message.success("Document uploaded successfully!");
          // Refresh the data by refetching company details
          companyService.getById(id)
            .then((x) => {
              const company = x.content;
              setCompany(company);
              
              const existingDocs = company.subApplicationDocuments || [];
              const allDocumentTypes = TiepDocumentType.map(docType => {
                const existingDoc = existingDocs.find(doc => doc.applicationDocumentType === docType.value);
                return {
                  key: docType.value,
                  documentType: docType.name,
                  documentTypeValue: docType.value,
                  documentName: existingDoc?.documentName || '',
                  applicationStatus: existingDoc?.applicationStatus || '',
                  checked: existingDoc?.checked || false,
                  comment: existingDoc?.comment || '',
                  existingDocument: existingDoc,
                  hasDocument: !!existingDoc
                };
              });
              
              setDocumentData(allDocumentTypes);
            });
        })
        .catch((error) => {
          message.error("Upload failed: " + (error.message || error));
        })
        .finally(() => {
          setUploadingFiles(prev => ({ ...prev, [documentTypeValue]: false }));
          // Clear the selected file after upload
          setSelectedFiles(prev => {
            const newState = { ...prev };
            delete newState[documentTypeValue];
            return newState;
          });
        });
    };
    
    reader.readAsDataURL(file);
    return false; // Prevent default upload behavior
  };

  const getAdditionalButtons = () => {
    let buttons = [];
    if (Auth.hasRole(Auth.getProfile(), ["SAU", "CAU"])) {
      buttons.push(
        <Link to={`${path}/add`.replace(":id", id)} key="1">
          <Button>Add Document</Button>
        </Link>
      );
    }
    return buttons;
  };

  const columns = [
    {
      title: "Document Type",
      dataIndex: "documentType",
      key: "documentType",
      width: "25%",
      render: (text) => <span style={{ fontWeight: "bold" }}>{text}</span>,
    },
    {
      title: "Document Name",
      dataIndex: "documentName",
      key: "documentName",
      width: "15%",
      render: (text, record) => (
        record.hasDocument ? (
          <span style={{ fontWeight: "bold" }}>{text}</span>
        ) : (
          '-'
        )
      ),
    },
    {
      title: "File Upload",
      key: "fileUpload",
      width: "20%",
      render: (text, record) => {
        if (record.hasDocument) {
          return (
            <span style={{ color: "#52c41a", fontWeight: "bold" }}>
              ✓ File Uploaded
            </span>
          );
        } else {
          return (
            <Input
              type="file"
              name="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setSelectedFiles(prev => ({
                    ...prev,
                    [record.documentTypeValue]: file
                  }));
                }
              }}
              style={{ 
                fontSize: '12px',
                padding: '4px 8px'
              }}
            />
          );
        }
      },
    },
    /* {
      title: "Status",
      dataIndex: "applicationStatus",
      key: "applicationStatus",
      width: "15%",
      render: (applicationStatus, record) => {
        if (!record.hasDocument) return <Tag color="blue">APPLICATION</Tag>;
        
        let color = "";
        if (applicationStatus === "APPROVAL") {
          color = "green";
        } else if (applicationStatus === "CLOSURE") {
          color = "red";
        } else if (applicationStatus === "APPLICATION") {
          color = "blue";
        } else if (applicationStatus === "EXTENSIONS") {
          color = "purple";
        } else if (applicationStatus === "AMENDMENTS") {
          color = "orange";
        }
        return <Tag color={color}>{applicationStatus}</Tag>;
      },
    }, */
    /* {
      title: "Checked",
      dataIndex: "checked",
      key: "checked",
      render: (checked) => (
        <Tag color={checked ? "green" : "default"}>
          {checked ? "Yes" : "No"}
        </Tag>
      ),
    }, */
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      render: (text, record) => {
        if (record.hasDocument) {
          return (
            <Button 
              type="link" 
              size="small" 
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadDocument(record.existingDocument)}
            >
              Download
            </Button>
          );
        } else {
          const hasSelectedFile = selectedFiles[record.documentTypeValue];
          return (
            <Button 
              type="primary" 
              size="small" 
              icon={<UploadOutlined />}
              loading={uploadingFiles[record.documentTypeValue]}
              disabled={!hasSelectedFile}
              onClick={() => {
                const file = selectedFiles[record.documentTypeValue];
                if (file) {
                  handleFileUpload(file, record.documentTypeValue);
                }
              }}
            >
              Upload
            </Button>
          );
        }
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item href="/dashboard">
              <span>Dashboard</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item href="/companies">
              <span>Company Search</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item href={`/companies/${company.id}/summary`}>
              <span>Company: {company.code}</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Documents</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getAdditionalButtons()}
      >
        <Page inner error={error}>
          <TabMenu menu="document-upload" id={id} />
          <Table
            bordered
            loading={loading}
            rowKey="key"
            size="small"
            columns={columns}
            dataSource={documentData}
            pagination={false}
          />
        </Page>
      </PageHeader>
    </div>
  );
}

export default List;
